import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { BarPlot } from '@mui/x-charts/BarChart'
import { ChartContainer as ResponsiveChartContainer } from '@mui/x-charts/ChartContainer'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis'
import type { DatasetType } from '@mui/x-charts/internals'

import { H3, Text } from '@mxenabled/mxui'
import { ChevronRight } from '@mxenabled/mx-icons'

import { getMonthName } from '../../util/finstrongUtils'

import { CustomDoubleBarPlot } from './CustomDoubleBarPlot'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

export interface FinancialData {
  year: number
  month: number
  standard_spend: number
  income: number
  debt_spend: number
}

export interface ChartDataPoint {
  label: string
  main: number
  comparison: number
  formattedMain: string
  formattedComparison: string
  [key: string]: string | number
}

export interface DoubleBarChartProps {
  chartFor: 'spending_vs_income' | 'debt_payment_vs_income'
  data: FinancialData[]
  title: string
  buttonLabel?: string
  legendLabelMain: string
  legendLabelComparison: string
  onClick?: () => void
  totalMain?: string
  totalComparison?: string
}

/**
 * Formats a number as currency.
 * - Rounds small values.
 * - Abbreviates large values (e.g., 1.2k, 3.4M) above the threshold.
 *
 * @param amount - The number to format.
 * @param threshold - Value above which abbreviation is applied (default: 1000).
 * @returns Formatted currency string.
 */
const formatCurrencyCompact = (amount: number, threshold: number = 1000): string => {
  const format = Math.abs(amount) >= threshold ? '0.[0]a' : '0'
  return formatCurrency(amount, format)
}

const DoubleBarChart: React.FC<DoubleBarChartProps> = ({
  chartFor,
  data,
  title,
  buttonLabel,
  onClick,
  legendLabelMain,
  legendLabelComparison,
  totalComparison,
  totalMain,
}) => {
  const theme = useTheme()
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('xs'))

  // Transform financial data to chart format
  const chartData: ChartDataPoint[] = React.useMemo(
    () =>
      data
        .sort((a, b) => {
          // Sort by year first, then by month
          if (a.year !== b.year) {
            return a.year - b.year
          }
          return a.month - b.month
        })
        .map((item) => {
          const monthName = getMonthName(item.month)

          const chartConfig = {
            spending_vs_income: {
              main: item.standard_spend,
              comparison: item.income,
              formattedMain: formatCurrencyCompact(item.standard_spend),
              formattedComparison: formatCurrencyCompact(item.income),
            },
            debt_payment_vs_income: {
              main: item.income,
              comparison: item.debt_spend,
              formattedMain: formatCurrencyCompact(item.income),
              formattedComparison: formatCurrencyCompact(item.debt_spend),
            },
          }

          return {
            label: monthName,
            ...chartConfig[chartFor],
          }
        }),
    [data, chartFor],
  )

  const xAxis = {
    dataKey: 'label',
    scaleType: 'band' as const,
  }

  // Dynamic Y-axis scaling
  const maxValue = Math.max(...chartData.map((item) => Math.max(item.main, item.comparison)))
  const minValue = Math.min(...chartData.map((item) => Math.min(item.main, item.comparison)))

  // If both min and max values are 0, set max to 100
  const yAxisMax =
    minValue === 0 && maxValue === 0 ? 100 : Math.ceil((maxValue * 1.2) / 1000) * 1000
  const tickStep = yAxisMax / 3

  const yAxis = {
    scaleType: 'linear' as const,
    min: 0,
    max: yAxisMax,
    tickNumber: 4,
    tickMinStep: tickStep,
    valueFormatter: (value: number) => {
      if (value === 0) return '$0'
      return formatCurrency(value, '0.[0]a')
    },
  }

  return (
    <Card>
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          gap: 12,
          justifyContent: 'space-between',
          pb: 8,
          pt: 16,
          px: 16,
        }}
      >
        <H3>{title}</H3>
        {buttonLabel && (
          <Button
            endIcon={<ChevronRight />}
            onClick={onClick}
            size="small"
            sx={{
              pl: 8,
              height: 24,
            }}
            variant="text"
          >
            {buttonLabel}
          </Button>
        )}
      </Stack>

      <Stack sx={{ pb: 24, pl: 16, pr: 24 }}>
        {/* Legend */}
        <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 24, py: 4 }}>
          <Stack sx={{ gap: 8, alignItems: 'center', flexDirection: 'row' }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: 'chartMono.chartMono3',
                borderRadius: '2px',
              }}
            />
            <Stack sx={{ alignItems: 'center', flexDirection: 'row' }}>
              <Text variant="subtitle2">{legendLabelMain}&nbsp;</Text>
              {!isSmallMobile && totalMain && (
                <Text sx={{ fontWeight: 700 }} variant="subtitle2">
                  {totalMain}
                </Text>
              )}
            </Stack>
          </Stack>

          <Stack alignItems="center" direction="row" spacing={8}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: 'chartMono.chartMono5',
                borderRadius: '2px',
              }}
            />
            <Stack sx={{ alignItems: 'center', flexDirection: 'row' }}>
              <Text variant="subtitle2">{legendLabelComparison}&nbsp;</Text>
              {!isSmallMobile && totalComparison && (
                <Text sx={{ fontWeight: 700 }} variant="subtitle2">
                  {totalComparison}
                </Text>
              )}
            </Stack>
          </Stack>
        </Stack>

        <ResponsiveChartContainer
          colors={[
            `${theme.palette.chartMono.chartMono3}`,
            `${theme.palette.chartMono.chartMono5}`,
          ]}
          dataset={chartData as DatasetType}
          height={240}
          margin={{ top: 14, bottom: 10, right: 2, left: -2 }}
          series={[
            { dataKey: 'main', type: 'bar' as const },
            { dataKey: 'comparison', type: 'bar' as const },
          ]}
          xAxis={[xAxis]}
          yAxis={[yAxis]}
        >
          <BarPlot
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                return (
                  <CustomDoubleBarPlot
                    {...defaultProps}
                    color={ownerState.color}
                    data={chartData as any}
                    series={{
                      index: ownerState.dataIndex,
                      type: Number(ownerState.id.toString().split('-').pop()),
                    }}
                    style={{
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />

          <ChartsXAxis
            sx={() => ({
              '.MuiChartsAxis-line': {
                stroke: theme.palette.divider,
              },
              '.MuiChartsAxis-tick': {
                stroke: theme.palette.divider,
              },
              '.MuiChartsAxis-tickLabel': {
                transform: 'translateY(10px)',
              },
            })}
            tickLabelStyle={{
              fontSize: 11,
              fill: 'text.secondary',
            }}
          />
          <ChartsYAxis
            sx={() => ({
              '.MuiChartsAxis-line': {
                stroke: theme.palette.divider,
              },
              '.MuiChartsAxis-tick': {
                stroke: theme.palette.divider,
              },
              '.MuiChartsAxis-tickLabel': {
                transform: 'translateX(-8px)',
              },
            })}
            tickLabelStyle={{
              fontSize: 11,
              fill: 'text.secondary',
            }}
          />
        </ResponsiveChartContainer>
      </Stack>
    </Card>
  )
}

export default DoubleBarChart
